import React, { useEffect, useState, useCallback } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { LineChart, Trophy, Wallet, GraduationCap, Sun, Moon, LogOut, Flame, Zap, RefreshCw, ChevronDown, Sigma, Search, Sparkles, Bell } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";
import { toast } from "sonner";
import { Footer } from "./Footer";

const NAV = [
  { to: "/dashboard", label: "Dashboard", short: "Picks", icon: LineChart, testId: "nav-dashboard" },
  { to: "/track-record", label: "Track Record", short: "Record", icon: Trophy, testId: "nav-track-record" },
  { to: "/methodology", label: "Method", short: "Method", icon: Sigma, testId: "nav-methodology" },
  { to: "/portfolio", label: "Portfolio", short: "Folio", icon: Wallet, testId: "nav-portfolio" },
  { to: "/learn", label: "Learn", short: "Learn", icon: GraduationCap, testId: "nav-learn" },
];

function useTheme() {
  const [dark, setDark] = useState(() => {
    const s = localStorage.getItem("ss_theme");
    return s ? s === "dark" : true;
  });
  useEffect(() => {
    const root = document.documentElement;
    if (dark) root.classList.add("dark");
    else root.classList.remove("dark");
    localStorage.setItem("ss_theme", dark ? "dark" : "light");
  }, [dark]);
  return { dark, toggle: () => setDark((d) => !d) };
}

export default function Layout({ children }) {
  const { user, logout, refreshUser } = useAuth();
  const { dark, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [running, setRunning] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [alerts, setAlerts] = useState([]);
  const [unread, setUnread] = useState(0);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);

  const loadAlerts = useCallback(() => {
    if (!user) return;
    api.get("/alerts").then(({ data }) => { setAlerts(data.alerts || []); setUnread(data.unread || 0); }).catch(() => {});
  }, [user]);

  const loadSettings = useCallback(() => {
    if (!user) return;
    api.get("/alerts/settings").then(({ data }) => setSettings(data.settings)).catch(() => {});
  }, [user]);

  const saveSettings = async (partial) => {
    const next = { ...settings, ...partial };
    setSettings(next);
    try { await api.put("/alerts/settings", next); } catch { toast.error("Could not save settings"); }
  };

  const sendTestEmail = async () => {
    setSendingTest(true);
    try {
      const { data } = await api.post("/alerts/digest/test");
      toast.success(`Digest emailed to ${data.sent_to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send email");
    } finally { setSendingTest(false); }
  };

  useEffect(() => {
    loadAlerts(); loadSettings();
    if (!user) return;
    const id = setInterval(loadAlerts, 45000);
    return () => clearInterval(id);
  }, [user, loadAlerts, loadSettings]);

  const openAlerts = async () => {
    const next = !alertsOpen;
    setAlertsOpen(next);
    if (next && unread > 0) {
      try { await api.post("/alerts/read"); setUnread(0); setAlerts((a) => a.map((x) => ({ ...x, read: true }))); } catch {}
    }
  };

  const goSearch = (e) => {
    e.preventDefault();
    const t = searchQ.trim();
    navigate(t ? `/search?q=${encodeURIComponent(t.toUpperCase())}` : "/search");
    setSearchQ("");
  };

  const runScoring = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/run-scoring");
      toast.success(`Scoring complete — ${data.count} tickers scored (${data.source})`);
      window.location.reload();
    } catch (e) {
      toast.error("Failed to run scoring");
    } finally {
      setRunning(false);
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Top nav */}
      <header className="backdrop-blur-xl bg-background/80 border-b border-border/60 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2.5" data-testid="logo-btn">
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
              <LineChart className="h-5 w-5 text-primary-foreground" />
            </div>
            <span className="font-heading font-extrabold text-lg tracking-tight">Aqvoro</span>
          </button>

          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={n.testId}
                className={({ isActive }) => cn(
                  "px-3.5 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2",
                  isActive ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                )}>
                <n.icon className="h-4 w-4" /> {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <form onSubmit={goSearch} className="hidden lg:flex items-center relative">
              <Search className="absolute left-2.5 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              <input data-testid="header-search-input" value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
                placeholder="AI search ticker…"
                className="h-9 w-44 pl-8 pr-3 rounded-lg bg-secondary/60 border border-border text-sm font-mono placeholder:font-sans placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40" />
            </form>
            <Button data-testid="header-search-btn-mobile" onClick={() => navigate("/search")} size="icon" variant="ghost" className="h-9 w-9 lg:hidden">
              <Sparkles className="h-4 w-4 text-primary" />
            </Button>

            {user && (
              <div className="hidden sm:flex items-center gap-2" data-testid="gamify-pill">
                <span className="flex items-center gap-1 text-sm font-mono font-semibold text-indigo-400 bg-indigo-500/10 border border-indigo-500/20 rounded-full px-2.5 py-1">
                  <Flame className={cn("h-3.5 w-3.5", user.streak > 0 && "ss-flame")} /> {user.streak || 0}
                </span>
                <span className="flex items-center gap-1 text-sm font-mono font-semibold text-primary bg-primary/10 border border-primary/20 rounded-full px-2.5 py-1">
                  <Zap className="h-3.5 w-3.5" /> {user.points || 0}
                </span>
              </div>
            )}

            {user?.role === "admin" && (
              <Button data-testid="run-scoring-btn" onClick={runScoring} disabled={running}
                size="sm" variant="outline" className="gap-1.5 border-primary/30 text-primary hover:bg-primary/10">
                <RefreshCw className={cn("h-3.5 w-3.5", running && "animate-spin")} />
                <span className="hidden lg:inline">Run Scoring</span>
              </Button>
            )}

            {user && (
              <div className="relative">
                <Button data-testid="alerts-bell-btn" onClick={openAlerts} size="icon" variant="ghost" className="h-9 w-9 relative">
                  <Bell className="h-4 w-4" />
                  {unread > 0 && (
                    <span data-testid="alerts-unread-badge"
                      className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                      {unread}
                    </span>
                  )}
                </Button>
                {alertsOpen && (
                  <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto rounded-xl border border-border bg-popover shadow-lg p-2 z-50"
                    onMouseLeave={() => setAlertsOpen(false)} data-testid="alerts-panel">
                    <div className="px-2 py-2 border-b border-border mb-1">
                      <span className="text-sm font-semibold">Watchlist Alerts</span>
                    </div>
                    {settings && (
                      <div className="px-2 pb-2 mb-1 border-b border-border space-y-2" data-testid="alert-settings">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-muted-foreground">Sensitivity</span>
                          <select data-testid="alert-sensitivity" value={settings.score_delta}
                            onChange={(e) => saveSettings({ score_delta: Number(e.target.value) })}
                            className="text-xs bg-secondary border border-border rounded-md px-2 py-1 focus:outline-none">
                            <option value={8}>Standard · ±8</option>
                            <option value={15}>Big moves · ±15</option>
                            <option value={25}>Major only · ±25</option>
                          </select>
                        </div>
                        <label className="flex items-center justify-between gap-2 cursor-pointer">
                          <span className="text-xs text-muted-foreground">News sentiment alerts</span>
                          <input type="checkbox" data-testid="alert-sentiment-toggle" checked={settings.sentiment}
                            onChange={(e) => saveSettings({ sentiment: e.target.checked })} className="accent-primary h-4 w-4" />
                        </label>
                        <label className="flex items-center justify-between gap-2 cursor-pointer">
                          <span className="text-xs text-muted-foreground">Daily email digest</span>
                          <input type="checkbox" data-testid="alert-email-toggle" checked={settings.email_digest}
                            onChange={(e) => saveSettings({ email_digest: e.target.checked })} className="accent-primary h-4 w-4" />
                        </label>
                        <button data-testid="send-test-email-btn" onClick={sendTestEmail} disabled={sendingTest}
                          className="w-full text-xs font-medium rounded-md border border-primary/30 text-primary hover:bg-primary/10 py-1.5 transition-colors">
                          {sendingTest ? "Sending…" : "Email me a test digest"}
                        </button>
                      </div>
                    )}
                    {alerts.length === 0 ? (
                      <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                        No alerts yet. Star tickers on AI Search to get notified when their score or news shifts.
                      </p>
                    ) : (
                      alerts.map((a) => (
                        <div key={a.alert_id} data-testid={`alert-${a.ticker}`} className="px-2 py-2 rounded-lg hover:bg-secondary flex gap-2">
                          <span className={cn("mt-1 h-2 w-2 rounded-full shrink-0", a.type === "sentiment" ? "bg-indigo-400" : "bg-primary")} />
                          <div>
                            <p className="text-sm leading-snug">{a.message}</p>
                            <p className="text-[10px] text-muted-foreground mt-0.5">{new Date(a.ts).toLocaleString()}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            <Button data-testid="theme-toggle" onClick={toggle} size="icon" variant="ghost" className="h-9 w-9">
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>

            {user ? (
              <div className="relative">
                <button data-testid="user-menu-btn" onClick={() => setMenuOpen((o) => !o)}
                  className="flex items-center gap-1.5 rounded-full pl-1 pr-2 py-1 hover:bg-secondary transition-colors">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="h-7 w-7 rounded-full object-cover" />
                  ) : (
                    <div className="h-7 w-7 rounded-full bg-secondary border border-border flex items-center justify-center text-xs font-bold">
                      {(user.name || user.email || "U")[0].toUpperCase()}
                    </div>
                  )}
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
                {menuOpen && (
                  <div className="absolute right-0 mt-2 w-56 rounded-xl border border-border bg-popover shadow-lg p-2 z-50"
                    onMouseLeave={() => setMenuOpen(false)}>
                    <div className="px-2 py-2 border-b border-border mb-1">
                      <p className="text-sm font-semibold truncate">{user.name || "User"}</p>
                      <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                    </div>
                    <button data-testid="logout-btn" onClick={doLogout}
                      className="w-full flex items-center gap-2 px-2 py-2 text-sm rounded-lg hover:bg-secondary text-red-400">
                      <LogOut className="h-4 w-4" /> Log out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Button data-testid="header-login-btn" size="sm" onClick={() => navigate("/login")}
                className="bg-primary text-primary-foreground hover:bg-primary/90">Sign in</Button>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-32 md:pb-24">
        {children}
      </main>

      <Footer className="pb-28 md:pb-16" />

      {/* Persistent footer disclaimer */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-amber-500/10 border-t border-amber-500/20 backdrop-blur-md
                      md:pb-0 pb-[60px]">
        <p className="max-w-7xl mx-auto px-4 py-2 text-center text-[11px] sm:text-xs text-amber-600 dark:text-amber-400/90 font-medium">
          ⚠️ For educational purposes only — not financial advice. No real money is involved. Do your own research.
        </p>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-background/95 backdrop-blur-xl border-t border-border grid grid-cols-5">
        {NAV.map((n) => {
          const active = location.pathname === n.to;
          return (
            <NavLink key={n.to} to={n.to} data-testid={`${n.testId}-mobile`}
              className={cn("flex flex-col items-center justify-center gap-0.5 py-2.5 text-[11px] font-medium",
                active ? "text-primary" : "text-muted-foreground")}>
              <n.icon className="h-5 w-5" /> {n.short}
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
